import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { MessageSchema } from './primitives.js';
const c = initContract();
/**
 * ADM-04 — NOTIFICATIONS (spec §15.5).
 *
 * "This screen lets you read the things the system wants to tell you. You use
 * it when the red dot appears on the bell in the top bar and you want to know
 * why."
 *
 * ── THE FOUR RULES THAT DECIDE THIS FILE'S SHAPE ───────────────────────────
 *
 * BR-ADM-04-04 — "A person must see only their own notifications. There must
 *   be no way to read anybody else's, INCLUDING FOR THE OWNER." So no route
 *   here takes a user id, in a path, a query or a body. There is nothing to
 *   forge, which is a stronger guarantee than a check.
 *
 * BR-ADM-04-05 — "A notification must not be created for a person whose role
 *   cannot see the record behind it." That is a rule about the WRITE side and
 *   it is enforced there (`notification-recipients.ts` resolves a permission
 *   to accounts). It has a consequence here: because a role can change after a
 *   row is written, opening one re-checks (ERR-ADM-04-02).
 *
 * BR-ADM-04-06 — "Opening a notification must mark it read and open the record
 *   it names. The prototype's rows are inert, and a build must not copy that."
 *   Hence `open`, which does both in one call.
 *
 * BR-ADM-04-08 — "A notification must never be the only place a fact lives."
 *   So a row carries a `kind`, a few `params` and a LINK, and never a
 *   free-text sentence: the sentence is rendered from the dictionaries at read
 *   time, in the reader's own language, and the fact itself lives on the
 *   record the link points at.
 *
 * ── WHY THE WIRE CARRIES A KIND AND NOT A SENTENCE ─────────────────────────
 *
 * Every screen in this product is bilingual and the dictionaries are checked
 * for parity by a test (§21.17). A sentence frozen at raise time is frozen in
 * one language: a person who switches to Arabic would read an English backlog
 * for the next six months. So the API returns `{kind, params}` and the web
 * renders `notifications.body.<kind>` with the params interpolated — which
 * also puts notification wording under the same parity test as everything
 * else, and makes BR-ADM-04-13 ("must name the thing, the number, and the
 * party where there is one") a property of a dictionary entry a reviewer can
 * read rather than of a template string buried in a service.
 */
// ── The five categories (§15.5 §5) ──────────────────────────────────────────
/**
 * "Note that Expire Date is the only red category, and that inventory alerts
 * are amber rather than red. That matches the colour meanings used across the
 * whole product." Icons and colours are the web's business; the ORDER is the
 * spec's and is preserved, because it is also the chip order.
 */
export const NOTIFICATION_CATEGORIES = [
    'banks',
    'parties',
    'inventory',
    'expiry',
    'reminder',
];
export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
// ── What a row can point at (BR-ADM-04-06) ──────────────────────────────────
/**
 * The kinds of record a notification can open. Deliberately a small closed set
 * rather than "any table": the web maps each one to a route, and a link type
 * with no route is a row that cannot be opened — the exact defect
 * BR-ADM-04-06 was written against.
 *
 * `stock_low` and `stock_out` point at a LIST rather than one record, because
 * the events behind them are aggregates (EC-15-12 — see NOTIFICATION_KINDS).
 */
export const NOTIFICATION_LINK_TYPES = [
    'invoice',
    'partner',
    'item',
    'incoming_check',
    'outgoing_check',
    'stock_low',
    'stock_expiring',
    'leave_request',
    'task',
];
export const NotificationLinkTypeSchema = z.enum(NOTIFICATION_LINK_TYPES);
/**
 * §15.5 §11's table, as built. Ten of its thirteen rows.
 *
 * ── AN ARITHMETIC CORRECTION, LEFT IN PLACE RATHER THAN QUIETLY FIXED ──────
 *
 * This header said "ten of its thirteen rows" while arguing THREE absences and
 * defining NINE kinds, and 13 − 3 = 10. The missing tenth was ROW 5 — "a
 * customer or supplier statement is generated · whoever asked for it" — which
 * was neither built nor argued absent, so the list read as complete and was
 * not. It is `statement_sent` below, and the count is now honest.
 *
 * The lesson is worth more than the row: a list that states its own total and
 * enumerates its own exceptions can be checked by addition, and nobody added.
 * An absence WITH a reason is a decision; an absence with neither a member nor
 * a reason is invisible.
 *
 * WHAT IS HERE AND WHY EACH ONE IS ANSWERABLE TODAY — every kind below is
 * raised from a record this build actually keeps, which is BR-ADM-04-08 read
 * as a build constraint rather than as a runtime check.
 *
 * THE THREE THAT ARE NOT, each with the reason rather than an omission:
 *
 *   §11 row 3, "a bank feed fails to import" — there is no bank feed. FIN-09's
 *     import is a file the user chooses; a *feed* is a vendor integration and
 *     ADM-02's registry lists it as `TODO(deploy)`. A notification for an
 *     event nothing can raise would be a dictionary entry and a dead branch.
 *
 *   §11 row 10, "a contract, lease or licence is close to expiring" — the data
 *     model has no contract, lease or licence record. ADM-04's own example
 *     ("Warehouse lease for Branch 2 expires end of month") names a record
 *     type §5 and PART 17 never define. It belongs with whatever wave creates
 *     one.
 *
 *   §11 row 12, "a tax return or filing is due" — FIN-08 keeps FILINGS, not a
 *     filing CALENDAR: there is no record of when the next return falls due,
 *     and inventing one from `fiscal.cycle_*` would make the notification the
 *     only place that fact lived, which is exactly BR-ADM-04-08's prohibition.
 *
 * §11 row 13 ("something is waiting for your approval") is here, wired to the
 * leave flow, which is the one approval in the build with a NAMED APPROVER on
 * the record (HR-04; `leave.service.ts` routes to `approver_id`). The other
 * eleven flows of 16.5 approve by permission rather than by name, so "the
 * named approver" would have to become "everybody who could approve" — a
 * different rule, and one §11 does not state. Extending it is a follow-on.
 *
 * THE ARITHMETIC, KEPT CHECKABLE — and the whole reason the correction above
 * is worth its paragraph. THIRTEEN kinds are defined below: §11's ten, plus
 * THREE that §11 does not contain and each of which argues its absence from
 * the table in its own docblock — `statement_run_sent`, `task_reminder`, and
 * `invoice_clearance_cancelled` (ruling A6). 10 + 3 = 13, and a reader who
 * adds them up and gets something else has found a defect rather than a
 * typo.
 */
export const NOTIFICATION_KINDS = {
    // ── Banks and Checks ─────────────────────────────────────────────────────
    /** §11 row 1 — "A cheque clears · The accountant who banked it." */
    check_cleared: {
        category: 'banks',
        link: 'incoming_check',
        params: ['code', 'party', 'amount', 'currency', 'account'],
    },
    /** §11 row 2 — "An outgoing cheque is due to post tomorrow · Every Accountant." */
    check_due_tomorrow: {
        category: 'banks',
        link: 'outgoing_check',
        params: ['code', 'party', 'amount', 'currency'],
    },
    // ── Parties ──────────────────────────────────────────────────────────────
    /**
     * §11 row 4 — "A customer goes over their credit limit on a new order · The
     * salesperson on the order, and every Accountant." The example body in §10
     * is the one that ties a notification to a company setting: the `$50,000` in
     * it is `treasury.debt_limit`, which is why `limit` is a param.
     */
    credit_limit_exceeded: {
        category: 'parties',
        link: 'partner',
        params: ['party', 'limit'],
    },
    /** §11 row 6 — "An invoice passes its due date unpaid · The salesperson on it, and every Accountant." */
    invoice_overdue: {
        category: 'parties',
        link: 'invoice',
        params: ['number', 'party', 'amount', 'currency', 'days'],
    },
    /**
     * §11 row 5 — "A customer or supplier statement is generated · Whoever asked
     * for it."
     *
     * The audience column is the narrowest in the whole table and it is meant
     * literally: one person, the one who pressed Send Statement. CRM-04 §9 is
     * the argument — "Send Statement is the one outward-facing action… because
     * it puts a document in front of the customer" — so the notification is a
     * receipt for an act somebody performed, not an announcement to the office.
     * `userIfHolding` supplies the second half of BR-ADM-04-05: being the person
     * who asked is not the same as still being allowed to open the partner.
     *
     * `address` is on the row because it is the fact a receipt exists to carry.
     * "A statement was sent" invites the question this body answers — MD-01 lets
     * a partner's address be edited, so the address it went to is NOT
     * re-derivable from the partner record tomorrow. It is still not the only
     * place the fact lives (BR-ADM-04-08): `email_outbox` holds the rendered
     * message, its recipient and its delivery status, and the link opens the
     * partner whose statement it is.
     */
    statement_sent: {
        category: 'parties',
        link: 'partner',
        params: ['party', 'amount', 'currency', 'address'],
    },
    /**
     * THE BULK RUN'S OWN LINE — delegated ruling D15 (26 Aug 2026), which makes
     * the monthly statement run "an ADM-01 opt-in, default off".
     *
     * Not a §11 row: §11 has no row for it, because the run did not exist when
     * §11 was written. It is here because D15's other half — "in-app for
     * everything" — is a promise that a thing the system did on your behalf is
     * findable in the product, and a scheduled run nobody is watching is exactly
     * the case that promise is for.
     *
     * AGGREGATE, under EC-15-12: "one action touching 400 items — a planning run
     * — must raise ONE notification saying '40 items need reordering', not 40
     * notifications." A tenant with 300 customers would otherwise wake every
     * `crm.view` holder to 300 rows on the first of the month, which is the
     * defect that edge case is written against, at its worst scale.
     *
     * Its `linkId` is null and `pathForNotification` already answers that for
     * `partner` — a null id routes to the partners LIST, which is where a reader
     * checks who was and was not written to. Same shape as `stock_low`, and no
     * new link type is needed to get it.
     */
    statement_run_sent: {
        category: 'parties',
        link: 'partner',
        params: ['example', 'count'],
    },
    // ── Inventory ────────────────────────────────────────────────────────────
    /**
     * §11 row 7 — "An item drops below its reorder point."
     *
     * AGGREGATE, and that is EC-15-12 rather than a shortcut: "one action
     * touching 400 items — a planning run — must raise ONE notification saying
     * '40 items need reordering', not 40 notifications." So `count` is the
     * subject and the link is the low-stock LIST. `example` names one item so
     * the body still passes BR-ADM-04-13's "name the thing" ("Printer Toner
     * TN-2420 and 3 others…" reads as a fact; "4 items need reordering" reads as
     * a statistic).
     */
    stock_below_reorder: {
        category: 'inventory',
        link: 'stock_low',
        params: ['example', 'count'],
    },
    /** §11 row 8 — "An item reaches zero." Aggregated for the same reason. */
    stock_out: {
        category: 'inventory',
        link: 'stock_low',
        params: ['example', 'count'],
    },
    // ── Expire Date ──────────────────────────────────────────────────────────
    /**
     * §11 row 9 — "A stock batch is close to its expiry date." §10's own example
     * is the model for the body: "12 units of Amoxicillin 500mg expire on Jul
     * 14, 2026 — clear before then."
     */
    batch_expiring: {
        category: 'expiry',
        link: 'stock_expiring',
        params: ['item', 'quantity', 'date'],
    },
    /**
     * §11 row 11 — "A cheque reaches the reminder window set in company
     * settings · Every Accountant." §12: "How often a cheque reminder fires is
     * already a company setting: Check reminder before, a number of days,
     * default 3."
     */
    check_reminder: {
        category: 'expiry',
        link: 'outgoing_check',
        params: ['code', 'party', 'amount', 'currency', 'days'],
    },
    // ── Reminder ─────────────────────────────────────────────────────────────
    /** §11 row 13 — "Something is waiting for your approval · The named approver." */
    approval_waiting: {
        category: 'reminder',
        link: 'leave_request',
        params: ['requester', 'kind', 'days'],
    },
    /**
     * CRM-03's own reminder, finally sent — Q-CRM-03-2 (spec line 23062):
     * "Reminders are labelled **(mock)** and nothing is sent. → Build them: a
     * notification to the assignee at the due time minus the lead time,
     * defaulting to 15 minutes." That default is not a new setting: it is on the
     * row already (`tasks.reminder_minutes`, DDL default 15, restated in the
     * service as BR-CRM-03-02's server-supplied default).
     *
     * Not a §11 row, and the same argument `statement_run_sent` makes applies:
     * §11 was written before this existed. Category `reminder` is §11's own
     * bucket for "everything else — filings, deadlines" (§5's category table,
     * spec line 33101), and a task's due date is a deadline.
     *
     * ── WHY THE SENTENCE NAMES THE DUE TIME AND NOT THE LEAD TIME ─────────────
     *
     * BR-ADM-04-13 wants "the thing, the number, and the party where there is
     * one". The thing is the title; the number is the moment it falls due. The
     * LEAD time is when this row was raised, not anything the reader has to act
     * on — and §15's zero case ("a reminder lead time of 0 minutes means 'at the
     * due time'") makes a body that recited it read as nonsense exactly where
     * the spec is most explicit. The lead time is still visible where it was
     * set: the row bell renders `crm.minBefore`.
     *
     * `due` arrives ALREADY FORMATTED, 'YYYY-MM-DD HH:MM' in the COMPANY's own
     * zone. Every other param in this table is a raw value the web formats, and
     * this one is the exception because of GEN-85/N-16 — there is exactly one
     * clock and no screen may read the browser's. A raw instant here would be
     * rendered by whatever zone the reader's laptop is set to, which is the one
     * answer the rule forbids.
     */
    task_reminder: {
        category: 'reminder',
        link: 'task',
        params: ['title', 'due'],
    },
    /**
     * BR-SAL-04-11 / GEN-134, raised by ruling A6's invoice cancel act.
     *
     * ── WHY THIS IS A NOTIFICATION AND NOT A TRANSMISSION ─────────────────────
     *
     * BR-SAL-04-11 says the cancellation "must also send a cancellation to the
     * authority", and GEN-134 forbids cancelling a cleared invoice "without
     * also cancelling its clearance". The build cannot transmit: BR-FIN-08-01
     * keeps every country's rules out of the code, no jurisdiction adapter is
     * implemented, and BR-FIN-08-05 gives an Accepted record no action at all.
     * PART 17.3.1 row 9 says what to do instead, in the spec's own words —
     * "Follow the authority's own cancellation process, which produces another
     * filing." That is a HUMAN act, so the honest build tells the humans who
     * must perform it, which is also decision D15's ruling ("in-app for
     * everything").
     *
     * The alternative — cancelling silently and leaving the authority holding a
     * live invoice the company has voided — is the exact divergence GEN-134
     * exists to forbid, and it would be invisible.
     *
     * CATEGORY: `reminder`, which §5's own table defines as "everything else —
     * filings, deadlines" (spec line 33101). A clearance cancellation is a
     * filing. §11 row 12 ("a tax return or filing is due · Every Accountant")
     * is the nearest audience the table names and this borrows its bucket, but
     * it is deliberately NOT that row: row 12 wants a filing CALENDAR the build
     * does not keep, and this fires off a record the build does keep — the
     * invoice, which is BR-ADM-04-08 satisfied by construction.
     *
     * LINK: `invoice` — the invoice that was cancelled. Not the submission
     * record: BR-ADM-04-08 wants the row to open "a record carrying the same
     * fact", and the fact is the cancellation, which lives on the invoice. The
     * compliance strip is one click from there.
     *
     * PARAMS: BR-ADM-04-13 — "name the thing, the number, and the party".
     */
    invoice_clearance_cancelled: {
        category: 'reminder',
        link: 'invoice',
        params: ['number', 'party', 'amount', 'currency'],
    },
};
export const ALL_NOTIFICATION_KINDS = Object.keys(NOTIFICATION_KINDS);
export function notificationKindDef(kind) {
    return NOTIFICATION_KINDS[kind];
}
export function isNotificationKind(value) {
    return Object.hasOwn(NOTIFICATION_KINDS, value);
}
// ── Paging (§18) ────────────────────────────────────────────────────────────
/**
 * "Somebody back from a month away may have thousands. The prototype has no
 * paging. A Load more button loading 25 at a time MUST be added."
 *
 * ── WHY THIS LIST DOES NOT USE THE SHARED OFFSET CONVENTION ───────────────
 *
 * `pagination.ts` decides offset paging for every list in the product and
 * reserves keyset paging for exactly one shape — "an infinite-scroll feed with
 * no total and no filters". This is that shape, and the exception is argued in
 * THAT file rather than here, so somebody reading the convention meets its one
 * exception without having to find this screen.
 *
 * The short version: an inbox grows at the head while it is open, so an offset
 * would show a row twice or skip one on every Load more; §18 asks for an
 * append rather than a jump; and there is no total to print, because
 * BR-ADM-04-03's per-chip count is UNREAD and not the size of the list.
 */
export const NOTIFICATION_PAGE_SIZE = 25;
// ── Wire shapes ─────────────────────────────────────────────────────────────
export const NotificationSchema = z
    .object({
    id: z.uuid(),
    category: NotificationCategorySchema,
    kind: z.string(),
    /**
     * The sentence's nouns and numbers. Values are strings and numbers only —
     * a nested object here would be a fact living in a notification, which
     * BR-ADM-04-08 forbids, and would have nowhere to render.
     *
     * MONEY IS A PLAIN DECIMAL STRING WITH ITS CURRENCY CODE BESIDE IT, never
     * a JS number (§2.1) and never pre-formatted. §18's foreign-currency rule
     * is why the code travels: "an amount in a notification must be shown in
     * the DOCUMENT'S OWN currency with its own symbol, and must not be
     * converted." Formatting happens where every other amount in the product
     * is formatted — `apps/web/src/lib/format.ts` — so a notification and the
     * document it points at cannot render the same figure two ways.
     */
    params: z.record(z.string(), z.union([z.string(), z.number()])),
    linkType: NotificationLinkTypeSchema.nullable(),
    linkId: z.uuid().nullable(),
    /** ISO instant; §5 field 9 renders it as "40 min ago" in the company zone. */
    createdAt: z.iso.datetime(),
    /** §7 — two states, and the row's blue dot and 4% wash follow this one flag. */
    read: z.boolean(),
})
    .strict();
/**
 * BR-ADM-04-01 and BR-ADM-04-03 in one shape: the bell's dot ("It must not
 * show a count") and the chip badges ("count UNREAD only, and a chip with
 * nothing unread must show no badge at all").
 *
 * `unread` is per category AND in total, because the All chip's badge is the
 * total and F-80 defines it that way. The bell reads `unread.total > 0` and is
 * given no number to draw.
 */
export const NotificationSummarySchema = z
    .object({
    total: z.number().int().min(0),
    byCategory: z.record(NotificationCategorySchema, z.number().int().min(0)),
})
    .strict();
export const NotificationListQuerySchema = z
    .object({
    /** §6 — "Exactly one may be chosen. All is chosen when the screen opens." */
    category: NotificationCategorySchema.optional(),
    /**
     * The Load more cursor: the id of the last row already shown. A CURSOR and
     * not an offset, because this list grows at the head — an offset would show
     * a row twice, or skip one, every time something arrived between pages.
     */
    before: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(NOTIFICATION_PAGE_SIZE),
})
    .strict();
export const NotificationPageSchema = z
    .object({
    notifications: NotificationSchema.array(),
    /** Whether a Load more button should be drawn at all. */
    hasMore: z.boolean(),
    /** The chip badges, always for EVERY category — never just the filtered one. */
    unread: NotificationSummarySchema,
})
    .strict();
/**
 * BR-ADM-04-07 — "Mark all as read must apply only to what is in view under
 * the chosen chip, and must not ask for confirmation." So the category comes
 * with the request and an absent one means the All chip.
 */
export const MarkAllReadRequestSchema = z
    .object({ category: NotificationCategorySchema.optional() })
    .strict();
export const MarkAllReadResponseSchema = z
    .object({
    marked: z.number().int().min(0),
    unread: NotificationSummarySchema,
})
    .strict();
/**
 * What `open` answers. Three outcomes, and each is a sentence §17 already
 * wrote:
 *
 *   `ok`        — go there.
 *   `gone`      — ERR-ADM-04-01, "That record has been cancelled. There is
 *                 nothing to open." §17's remedy column: "Nothing; the row is
 *                 still marked read."
 *   `forbidden` — ERR-ADM-04-02, "You no longer have permission to open that
 *                 record." A role can be narrowed after a row is written, and
 *                 BR-ADM-04-05's promise ("not created for a person whose role
 *                 cannot see the record") has to survive that.
 *
 * IN ALL THREE the row is marked read, because BR-ADM-04-06 says opening marks
 * it read and does not make that conditional on the destination existing.
 */
export const NotificationOpenResponseSchema = z
    .object({
    outcome: z.enum(['ok', 'gone', 'forbidden']),
    linkType: NotificationLinkTypeSchema.nullable(),
    linkId: z.uuid().nullable(),
    /**
     * Extra route context the id alone does not carry — the low-stock list
     * needs no id, and a cheque needs to know which register it lives in
     * (which its link type already says). Kept as the raise-time params so the
     * client never has to fetch the record to know where to send the reader.
     */
    params: z.record(z.string(), z.union([z.string(), z.number()])),
    unread: NotificationSummarySchema,
})
    .strict();
export const NOTIFICATION_MESSAGES = {
    /**
     * ERR-ADM-04-01 / EC-15-11, word for word — and the edge case adds the half
     * a message cannot say on its own: clicking marks the notification READ and
     * the notification stays in the list, because it is part of the person's
     * history. Deleting it would be tidier and would erase the fact that they
     * were told.
     */
    recordGone: 'That record has been cancelled. There is nothing to open.',
    /**
     * ERR-ADM-04-02. EC-15-10 is the rule ABOVE it: a notification about a
     * record somebody may not open "must not be created at all" — a notification
     * must never be the way a fact is learned. This sentence is the floor for
     * the case the rule cannot prevent, where the permission was removed after
     * the notification was raised.
     */
    recordForbidden: 'You no longer have permission to open that record.',
    /** ERR-ADM-04-03 — §18 asks a build to word it per chip; the web does. */
    empty: 'No notifications here',
    /** ERR-ADM-04-05. */
    loadFailed: 'We could not load your notifications. Check your connection and try again.',
    /** A notification id that is not the caller's — indistinguishable from one that never existed. */
    notFound: 'No such notification.',
};
export const notificationsContract = c.router({
    /**
     * The bell, on every screen. Separate from `list` and deliberately tiny:
     * BR-ADM-04-01 needs one boolean on every page load for every signed-in
     * person, and making the shell fetch a page of rows to draw a 7-pixel dot
     * would be the most-called query in the product returning the most data it
     * does not use.
     */
    summary: {
        method: 'GET',
        path: '/api/notifications/summary',
        responses: { 200: NotificationSummarySchema, 401: MessageSchema },
        summary: 'BR-ADM-04-01/-03: unread counts, total and per category. The caller’s own only (BR-ADM-04-04); no permission (§9, BR-PERM-09).',
    },
    list: {
        method: 'GET',
        path: '/api/notifications',
        query: NotificationListQuerySchema,
        responses: { 200: NotificationPageSchema, 400: MessageSchema, 401: MessageSchema },
        summary: 'ADM-04 §4: the caller’s own notifications, newest first, 25 at a time (§18). Never anybody else’s (BR-ADM-04-04).',
    },
    markAllRead: {
        method: 'POST',
        path: '/api/notifications/read-all',
        body: MarkAllReadRequestSchema,
        responses: { 200: MarkAllReadResponseSchema, 400: MessageSchema, 401: MessageSchema },
        summary: 'BR-ADM-04-07: marks read exactly what is in view under the chosen chip. No confirmation.',
    },
    /**
     * POST for what looks like a read, for the same reason the audit export is
     * a POST: it WRITES — it marks the row read (BR-ADM-04-06) — and a GET with
     * a side effect is one any prefetcher or link preview may fire.
     */
    open: {
        method: 'POST',
        path: '/api/notifications/:id/open',
        pathParams: z.object({ id: z.uuid() }).strict(),
        body: z.object({}).strict(),
        responses: {
            200: NotificationOpenResponseSchema,
            401: MessageSchema,
            404: MessageSchema,
        },
        summary: 'BR-ADM-04-06: marks the row read and says where to go — or why not (ERR-ADM-04-01/-02).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=notifications.js.map